/**
 * Утилиты для работы с Infinite Query
 */

import type { InfiniteData } from '@tanstack/react-query';

/**
 * Базовый тип страницы для Infinite Query
 */
interface PageData<T> {
  data: T[];
  nextCursor?: unknown;
  hasMore?: boolean;
}

/**
 * Извлекает все элементы из InfiniteQuery результата
 * 
 * @param infiniteData - данные из useInfiniteQuery
 * @returns массив всех элементов из всех страниц
 * 
 * @example
 * const friends = flattenInfiniteQueryData(friendsData);
 */
export function flattenInfiniteQueryData<T>(
  infiniteData: InfiniteData<PageData<T>> | undefined
): T[] {
  if (!infiniteData) return [];
  return infiniteData.pages.flatMap(page => page.data);
}

/**
 * Извлекает элементы из InfiniteQuery и преобразует их
 * 
 * @param infiniteData - данные из useInfiniteQuery
 * @param mapper - функция преобразования элемента
 * @returns массив преобразованных элементов
 * 
 * @example
 * const simpleFriends = mapInfiniteQueryData(friendsData, friendship => ({
 *   id: friendship.profile.id,
 *   username: friendship.profile.username || '',
 * }));
 */
export function mapInfiniteQueryData<T, R>(
  infiniteData: InfiniteData<PageData<T>> | undefined,
  mapper: (item: T) => R
): R[] {
  if (!infiniteData) return [];
  return infiniteData.pages.flatMap(page => page.data.map(mapper));
}
